@let financialsObj = financials();

@if (financialsObj) {
  <div class="p-6">
    <!-- PRIMARY PRICE & NOTARY FEES -->
    <div
      class="mb-8 p-6 bg-indigo-a2 rounded-2xl border border-indigo-a4 flex flex-col sm:flex-row sm:items-center justify-between gap-6"
    >
      <div>
        <span class="text-xs font-black text-indigo-11 uppercase tracking-widest block mb-2"
          >Prix de vente</span
        >
        @if (financialsObj.sellingPriceCents) {
          <div class="flex flex-col">
            <span class="text-4xl md:text-5xl font-black text-indigo-11 flex items-baseline">
              {{ financialsObj.sellingPriceCents / 100 | number: '1.0-0' : 'fr-FR' }}
              <span class="text-2xl font-bold ml-1">€</span>
            </span>
            <div class="flex items-center gap-2 mt-1 flex-wrap">
              @if (pricePerSquareMeter(); as priceM2) {
                <span class="text-sm font-semibold text-indigo-11/80">
                  soit {{ priceM2 | number: '1.0-0' : 'fr-FR' }} €/m²
                </span>
              }
              @if (priceComparison(); as comp) {
                <span
                  class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold border {{
                    comp.colorClass
                  }}"
                  [matTooltip]="comp.label"
                >
                  <mat-icon class="text-xs! w-3! h-3! flex items-center justify-center">{{
                    comp.icon
                  }}</mat-icon>
                  {{ comp.badge }}
                </span>
              }
            </div>
          </div>
        } @else {
          <span class="text-3xl font-bold text-indigo-11/60">-</span>
        }
      </div>
      @if (financialsObj.taxesAndFees?.notaryFeesCents) {
        <div class="sm:text-right border-t sm:border-t-0 sm:border-l border-indigo-a4 pt-4 sm:pt-0 sm:pl-6">
          <div class="flex items-center gap-2 sm:justify-end mb-1">
            <span class="text-xs font-bold text-gray-10">Frais de notaire (est.)</span>
            @let notaryFeesPercentageVal = notaryFeesPercentage();
            @if (notaryFeesPercentageVal) {
              <span class="rounded-full bg-indigo-a3 px-2 py-0.5 text-[10px] font-bold text-indigo-11">
                {{ notaryFeesPercentageVal | number: '1.1-2' }} %
              </span>
            }
          </div>
          <span class="text-xl font-black text-gray-12">
            {{
              $safeNavigationMigration(financialsObj.taxesAndFees?.notaryFeesCents)! / 100
                | number: '1.0-0' : 'fr-FR'
            }}
            €
          </span>
        </div>
      }
    </div>

    <!-- DVF MARKET BENCHMARK SECTION -->
    @if (isDvfLoading()) {
      <div
        class="mb-8 p-4 rounded-2xl border border-gray-6 bg-gray-1 flex items-center justify-center gap-2.5 text-xs text-gray-10"
      >
        <mat-spinner diameter="16"></mat-spinner>
        <span>Recherche des repères réels DVF (DGFiP) du secteur...</span>
      </div>
    } @else if (dvfData(); as dvf) {
      <div class="mb-8 rounded-2xl border border-indigo-a4 bg-indigo-a2/40 p-5">
        <!-- En-tête DVF -->
        <div class="flex items-center justify-between gap-3 border-b border-indigo-a4/60 pb-3 flex-wrap">
          <div class="flex items-center gap-2.5">
            <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-a3 text-indigo-11">
              <mat-icon class="text-[18px]! w-4.5! h-4.5! flex items-center justify-center"
                >insights</mat-icon
              >
            </div>
            <div>
              <div class="flex items-center gap-1.5">
                <span class="font-bold text-gray-12 text-sm uppercase tracking-wider">
                  Repères de marché DVF (DGFiP)
                </span>
                <mat-icon
                  class="text-xs! w-3.5! h-3.5! text-gray-9 hover:text-indigo-11 cursor-help"
                  matTooltip="Données officielles de la Direction Générale des Finances Publiques recensant l'intégralité des transactions immobilières authentiques enregistrées chez les notaires."
                  matTooltipPosition="above"
                >
                  info
                </mat-icon>
              </div>
              <span class="text-[11px] text-gray-11 block">
                Actes notariés réels constatés dans votre secteur pour ce type de bien
              </span>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <span class="rounded-full bg-indigo-a3 px-2.5 py-1 text-xs font-bold text-indigo-11">
              {{ dvf.salesCount }} vente{{ dvf.salesCount > 1 ? 's' : '' }} actée{{
                dvf.salesCount > 1 ? 's' : ''
              }}
            </span>
          </div>
        </div>

        <!-- Cartes indicateurs DVF -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-4">
          <!-- Prix médian secteur -->
          <div class="rounded-xl border border-gray-6 bg-gray-1 p-3.5 flex flex-col justify-between">
            <div>
              <span class="text-[11px] font-bold uppercase tracking-wider text-gray-10 block">
                Prix médian secteur
              </span>
              <span class="text-xl font-bold text-gray-12 mt-1 block">
                {{ dvf.medianPriceM2 | number: '1.0-0' : 'fr-FR' }} €/m²
              </span>
            </div>
            @if (dvf.minPriceM2 && dvf.maxPriceM2) {
              <div
                class="mt-2 inline-flex text-[11px] text-gray-10 cursor-help flex-col"
                matTooltip="Fourchette constatée : prix au m² le plus bas et le plus haut enregistrés parmi les actes notariés du secteur."
                matTooltipPosition="above"
              >
                <span>Fourchette :</span>
                <span class="font-semibold text-gray-11">
                  {{ dvf.minPriceM2 | number: '1.0-0' : 'fr-FR' }} -
                  {{ dvf.maxPriceM2 | number: '1.0-0' : 'fr-FR' }} €/m²
                </span>
              </div>
            }
          </div>

          <!-- Valeur estimée pour la surface -->
          @if (dvfEstimatedPrice(); as estPrice) {
            <div class="rounded-xl border border-gray-6 bg-gray-1 p-3.5 flex flex-col justify-between">
              <div>
                <span class="text-[11px] font-bold uppercase tracking-wider text-gray-10 block">
                  Valeur estimée ({{ habitableSurface() }} m²)
                </span>
                <span class="text-xl font-bold text-primary mt-1 block">
                  {{ estPrice | number: '1.0-0' : 'fr-FR' }} €
                </span>
              </div>
              @if (dvfEstimatedPriceMin() && dvfEstimatedPriceMax()) {
                <div
                  class="mt-2 inline-flex flex-col text-[11px] text-gray-10 cursor-help"
                  matTooltip="Fourchette indicative appliquée à votre surface habitable selon les prix au m² du secteur."
                  matTooltipPosition="above"
                >
                  <span>Fourchette :</span>
                  <span class="font-semibold text-gray-11">
                    {{ dvfEstimatedPriceMin() | number: '1.0-0' : 'fr-FR' }} € à
                    {{ dvfEstimatedPriceMax() | number: '1.0-0' : 'fr-FR' }} €
                  </span>
                </div>
              }
            </div>
          }

          <!-- Positionnement prix de vente vs marché -->
          @if (priceComparison(); as comp) {
            <div class="rounded-xl border border-gray-6 bg-gray-1 p-3.5 flex flex-col justify-between">
              <div>
                <span class="text-[11px] font-bold uppercase tracking-wider text-gray-10 block">
                  Positionnement marché
                </span>
                <div class="flex items-center gap-1.5 mt-1">
                  <span
                    class="inline-flex items-center gap-1 text-sm font-bold {{
                      comp.colorClass.split(' ')[0]
                    }}"
                  >
                    <mat-icon class="text-[16px]! w-4! h-4! flex items-center justify-center">{{
                      comp.icon
                    }}</mat-icon>
                    {{ comp.badge }}
                  </span>
                </div>
              </div>
              <span class="text-[11px] text-gray-11 mt-2 block leading-snug">
                {{ comp.label }}
              </span>
            </div>
          }
        </div>

        <!-- Dernières ventes réelles si disponibles -->
        @if (allRecentSales().length > 0) {
          <div class="mt-4 pt-3.5 border-t border-indigo-a4/40">
            <div class="flex items-center justify-between gap-2 mb-2.5">
              <span class="text-[11px] font-bold uppercase tracking-wider text-gray-10 block">
                Dernières transactions réelles enregistrées dans le secteur
              </span>
              @if (allRecentSales().length > 3) {
                <span class="text-[10px] text-gray-10">
                  {{ isRecentSalesExpanded() ? allRecentSales().length : '3' }} sur
                  {{ allRecentSales().length }} affichées
                </span>
              }
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              @for (sale of displayedRecentSales(); track sale.id_mutation || sale.date_mutation) {
                <div class="p-2.5 rounded-lg border border-gray-6 bg-gray-1/80 text-xs flex flex-col gap-0.5">
                  <div class="flex items-center justify-between gap-1 text-gray-10 text-[10px]">
                    <span>{{ sale.date_mutation | date: 'dd/MM/yyyy' }}</span>
                    @if (sale.type_local) {
                      <span class="uppercase font-semibold">{{ sale.type_local }}</span>
                    }
                  </div>
                  <div class="flex items-baseline justify-between gap-1 mt-0.5">
                    <span class="font-bold text-gray-12">
                      {{ sale.valeur_fonciere | number: '1.0-0' : 'fr-FR' }} €
                    </span>
                    @if (sale.price_m2) {
                      <span class="text-[11px] font-semibold text-indigo-11">
                        {{ sale.price_m2 | number: '1.0-0' : 'fr-FR' }} €/m²
                      </span>
                    }
                  </div>
                  <div class="text-[10px] text-gray-10">
                    @if (sale.surface_reelle_bati) {
                      <span>{{ sale.surface_reelle_bati }} m²</span>
                    }
                    @if (sale.nombre_pieces_principales) {
                      <span>
                        • {{ sale.nombre_pieces_principales }} pièce{{
                          sale.nombre_pieces_principales > 1 ? 's' : ''
                        }}</span
                      >
                    }
                  </div>
                </div>
              }
            </div>

            @if (allRecentSales().length > 3) {
              <div class="mt-3 flex justify-center">
                <button
                  type="button"
                  (click)="toggleRecentSalesExpand()"
                  class="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-indigo-a4 bg-indigo-a2 text-xs font-semibold text-indigo-11 hover:bg-indigo-a3 transition-colors cursor-pointer"
                >
                  <span>
                    {{
                      isRecentSalesExpanded()
                        ? 'Afficher moins'
                        : 'Voir les ' +
                          (allRecentSales().length - 3) +
                          ' autres ventes (' +
                          allRecentSales().length +
                          ' au total)'
                    }}
                  </span>
                  <mat-icon
                    class="text-sm! w-4! h-4! flex items-center justify-center transition-transform"
                    [class.rotate-180]="isRecentSalesExpanded()"
                  >
                    keyboard_arrow_down
                  </mat-icon>
                </button>
              </div>
            }
          </div>
        }

        <!-- Footer source officielle -->
        <div
          class="mt-3.5 flex items-center justify-between border-t border-indigo-a4/40 pt-2.5 text-xs text-gray-10 flex-wrap gap-2"
        >
          <div class="flex items-center gap-1 text-[11px]">
            <span>Données publiques certifiées</span>
            <span class="text-gray-9">•</span>
            <a
              href="https://app.dvf.etalab.gouv.fr/"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-0.5 font-semibold text-indigo-11 hover:underline underline-offset-2"
            >
              Consulter la base DVF Etalab
              <mat-icon class="text-[12px]! w-3! h-3! flex items-center justify-center">open_in_new</mat-icon>
            </a>
          </div>
          @if (dvf.fetchedAt) {
            <span class="text-[10px] text-gray-9">
              Données récupérées le {{ dvf.fetchedAt | date: 'dd/MM/yyyy' }}
            </span>
          }
        </div>
      </div>
    }

    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <!-- CHARGES SECTION -->
      <div>
        <div class="flex items-center gap-2 mb-4 text-gray-11">
          <mat-icon class="text-sm">receipt_long</mat-icon>
          <h4 class="font-bold uppercase tracking-wider text-xs m-0">Charges</h4>
        </div>

        <div class="space-y-3">
          <div class="flex justify-between items-center py-2 border-b border-gray-a4">
            <span class="text-gray-11 text-sm">Charges courantes</span>
            @if (financialsObj.chargesCents) {
              <span class="text-gray-12 font-bold"
                >{{ financialsObj.chargesCents / 100 | number: '1.0-1' : 'fr-FR' }} €<span
                  class="text-[10px] lowercase font-normal ml-0.5"
                  >/mois</span
                ></span
              >
            } @else {
              <span class="text-gray-10 font-bold">-</span>
            }
          </div>

          @if (isInCoOwnership() || financialsObj.coOwnershipChargesCents) {
            <div class="flex justify-between items-center py-2 border-b border-gray-a4">
              <span class="text-gray-11 text-sm">Charges copropriété</span>
              @if (financialsObj.coOwnershipChargesCents) {
                <span class="text-gray-12 font-bold"
                  >{{ financialsObj.coOwnershipChargesCents / 100 | number: '1.0-1' : 'fr-FR' }} €<span
                    class="text-[10px] lowercase font-normal ml-0.5"
                    >/mois</span
                  ></span
                >
              } @else {
                <span class="text-gray-10 font-bold">-</span>
              }
            </div>
          }
        </div>
      </div>

      <!-- TAXES SECTION -->
      <div>
        <div class="flex items-center gap-2 mb-4 text-gray-11">
          <mat-icon class="text-sm">gavel</mat-icon>
          <h4 class="font-bold uppercase tracking-wider text-xs m-0">Fiscalité</h4>
        </div>

        <div class="space-y-3">
          <div class="flex justify-between items-center py-2 border-b border-gray-a4">
            <span class="text-gray-11 text-sm">Taxe foncière</span>
            @if (financialsObj.taxesAndFees?.propertyTaxCents) {
              <span class="text-gray-12 font-bold"
                >{{
                  $safeNavigationMigration(financialsObj.taxesAndFees?.propertyTaxCents)! / 100
                    | number: '1.0-0' : 'fr-FR'
                }}
                €<span class="text-[10px] lowercase font-normal ml-0.5">/an</span></span
              >
            } @else {
              <span class="text-gray-10 font-bold">-</span>
            }
          </div>

          @if (isSecondaryResidence()) {
            <div class="flex justify-between items-center py-2 border-b border-gray-a4">
              <span class="text-gray-11 text-sm">Taxe d'habitation</span>
              @if (financialsObj.taxesAndFees?.housingTaxCents) {
                <span class="text-gray-12 font-bold"
                  >{{
                    $safeNavigationMigration(financialsObj.taxesAndFees?.housingTaxCents)! / 100
                      | number: '1.0-0' : 'fr-FR'
                  }}
                  €<span class="text-[10px] lowercase font-normal ml-0.5">/an</span></span
                >
              } @else {
                <span class="text-gray-10 font-bold">-</span>
              }
            </div>
          }

          @if (financialsObj.taxesAndFees?.wasteCollectionTaxCents) {
            <div class="flex justify-between items-center py-2 border-b border-gray-a4">
              <span class="text-gray-11 text-sm">Taxe ordures (TEOM)</span>
              <span class="text-gray-12 font-bold"
                >{{
                  $safeNavigationMigration(financialsObj.taxesAndFees?.wasteCollectionTaxCents)! / 100
                    | number: '1.0-0' : 'fr-FR'
                }}
                €<span class="text-[10px] lowercase font-normal ml-0.5">/an</span></span
              >
            </div>
          }
        </div>
      </div>

      <!-- PRECISIONS SECTION -->
      @if (financialsObj.precisions) {
        <div
          class="col-span-1 md:col-span-2 mt-2 p-4 bg-gray-2 rounded-xl border border-dashed border-gray-6 text-sm text-gray-11"
        >
          <div class="flex items-center gap-2 mb-2 text-indigo-11">
            <mat-icon class="text-sm">comment</mat-icon>
            <span class="font-bold uppercase text-[10px] tracking-widest">Précisions & Commentaires</span>
          </div>
          <p class="whitespace-pre-line m-0 leading-relaxed text-gray-12">
            {{ financialsObj.precisions }}
          </p>
        </div>
      }
    </div>
  </div>
}
